import { trpc } from '@tencent/tpro_rmonitor_web_server_common';
import { useControllableValue } from 'ahooks';
import { DatePicker, Select, Space } from 'antd';
import { Moment } from 'moment';
import React, { ReactNode, useMemo } from 'react';
import styles from './time-range-cascade.module.scss';
import TimeType = trpc.rmonitor.web_server.TimeType;

export interface IOption {
  value: string;
  label: ReactNode;
}

export type TimePresets = IOption & {
  presetsValue?: () => Moment | [Moment, Moment]
};

export interface ProDatePickerProps {
  // 埋点用
  tid?: string;
  // 是否启用时间窗口模式
  enableTimeWindow?: boolean;
  // 是否启用时间类型模式
  enableTimeType?: boolean;
  showTime?: boolean;
  timeQuickOptions?: TimePresets[];
  range?: boolean;
}

export interface ProDateValue {
  timeWindow: TimeType;
  timeQuick: string;
  date: Moment;
  dateRange: [Moment, Moment];
  timeType: string;
}

const TIME_WINDOW_OPTIONS = [
  { value: TimeType.TIME_TYPE_MINUTE5, label: '5分钟聚合' },
  { value: TimeType.TIME_TYPE_HOUR, label: '小时聚合' },
  { value: TimeType.TIME_TYPE_DAY, label: '按天聚合' },
];

const TIME_TYPE_OPTIONS = [
  {
    label: '上报时间',
    value: 'report',
  },
  {
    label: '发生时间',
    value: 'occur',
  },
];

const DEFAULT_FORMATTER_MINUTE = 'MM/DD HH:mm';

/**
 * 时间范围组件
 * @param props
 * @constructor
 */
export const ProBuglyDatePicker = (props: ProDatePickerProps) => {
  const { enableTimeWindow = true, enableTimeType = true, timeQuickOptions = [] } = props;
  const [value, setValue] = useControllableValue<ProDateValue>(props);

  const onTimeWindowChange = (timeWindow: TimeType) => {
    setValue(val => ({
      ...val,
      timeWindow,
      timeQuick: timeQuickOptions[0]?.value,
    }));
  };

  const onTimeQuickChange = (timeQuick: string, option: TimePresets | TimePresets[]) => {
    console.log('onTimeQuickChange', timeQuick, option);
    setValue(val => ({
      ...val,
      timeQuick,
    }));
  };

  const onTimeTypeChange = (timeType: string) => {
    setValue(val => ({
      ...val,
      timeType,
    }));
  };

  // 如果按天，不显示范围
  const isRange = useMemo(
    () => props.range
        || (value?.timeWindow !== TimeType.TIME_TYPE_MINUTE && value?.timeWindow !== TimeType.TIME_TYPE_HOUR),
    [value?.timeWindow, enableTimeWindow],
  );

  const renderDatePicker = () => (isRange
    ? <DatePicker.RangePicker
      className={styles.timeRange}
      value={value?.dateRange}
      format={DEFAULT_FORMATTER_MINUTE}
      showTime={props.showTime}
    />
    : <DatePicker
      className={styles.timeRange}
      value={value?.date as Moment}
    />);

  return (
    <div data-tid={props.tid} className={styles.timeRangeCascade}>
      <Space size={8}>
        {/* timeWindow */}
        {enableTimeWindow && <Select
          value={value?.timeWindow}
          options={TIME_WINDOW_OPTIONS}
          onChange={onTimeWindowChange}
          style={{ width: '100%' }}
          className={styles.timeWindow}
        />}

        {/* timeQuick */}
        <Select
          value={value?.timeQuick}
          options={timeQuickOptions}
          onChange={onTimeQuickChange}
          labelInValue
          style={{ width: '100%' }}
          className={styles.timeQuick}
        />

        {/* date picker */}
        {renderDatePicker()}

        {/* timeType */}
        {enableTimeType &&  <Select
          options={TIME_TYPE_OPTIONS}
          onChange={onTimeTypeChange}
          value={value?.timeType}
          className={styles.timeType}
        />}
      </Space>
    </div>
  );
};
